/**
 * taxCardPreview.js — what the item editor's card PREVIEW asks the backend to render.
 *
 * PURE ESM: no Vue, no axios, no service import, no DOM → importable by plain Node, which is the
 * point. The preview panel is a Vue component and a Vue component cannot be unit-tested here; the
 * two decisions that actually matter — *which* fields of the half-typed item travel to the server,
 * and *when* that counts as a change worth re-rendering for — are therefore kept in this file and
 * pinned by `tests/taxCardPreview.test.mjs`.
 *
 * ── The two rules everything here follows ────────────────────────────────────────────────────
 *
 * 1. A REQUEST CARRIES ONLY THE FIELDS ITS KIND ACTUALLY RENDERS FROM. SUMMARY never sends the
 *    SHORT_FORM boxes, SHORT_FORM never sends the summary template, FILING_CHOICE sends its config
 *    plus its own template, and SIMULATE — the one card kind with no template box — still sends
 *    `cardConfigJson` alone. Not for bandwidth: {@link previewSignature} is computed off the
 *    request, so a field that rides along unused would make typing in an unrelated box look like a
 *    change to the card and fire a render (and a network round-trip) per keystroke.
 *
 * 2. THE DRAFT WINS OVER THE PARSED OBJECT. The dialog keeps `shortFormConfig` as an OBJECT but the
 *    admin is typing into a JSON textarea whose live text is a draft that may not parse yet
 *    (`TaxQuestionItemV2Dialog.shortFormConfigText`). Stringifying the object would preview the last
 *    text that happened to parse — i.e. not what is on screen — so `opts.shortFormConfigText` is
 *    preferred whenever it is a non-blank string, and the server (which never throws, it reports)
 *    is left to say that a half-typed document is unreadable.
 *
 * 3. THE CARD COMES BACK AS A STRING AND IS PARSED HERE. The response carries `raw` — the card's
 *    JSON text — and no object beside it, exactly like every other card in the product
 *    (`TaxPlaygroundTurnDto.TaxCards` / `TaxLogV2TurnDto.TaxCards` are `string?`, read through
 *    `utils/taxCardMarker.js`). {@link parsePreviewCard} is this file's counterpart of that reader,
 *    and it is fail-soft for the same reason `splitTaxCards` is: a payload this side cannot read is
 *    something to REPORT, never something to throw over.
 */

/** The four `itemKind`s that produce a card, and therefore the only ones with anything to preview. */
export const PREVIEW_KINDS = ['SUMMARY', 'SHORT_FORM', 'SIMULATE', 'FILING_CHOICE'];

/**
 * The request's keys, in ONE fixed order — the whole basis of {@link previewSignature}.
 * Object key order is an accident of construction; a signature built on it would change when the
 * construction path changed rather than when the card did.
 */
const SIGNATURE_KEYS = [
  'itemKind',
  'cardConfigJson',
  'summaryTemplateJson',
  'shortFormConfigJson',
  'shortFormTemplateJson',
  'filingTemplateJson',
  'simulationTemplateJson',
  'fieldKey',
  'lang',
];

/** `itemKind` as it is compared: trimmed and upper-cased, '' for anything absent. */
function normKind(itemKind) {
  return String(itemKind == null ? '' : itemKind).trim().toUpperCase();
}

/**
 * One JSON box's text as it travels: the string exactly as typed (NOT trimmed — the server parses
 * it, and reformatting the author's document here would make the preview lie about it), `null` when
 * there is nothing in the box.
 */
function asJsonText(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  try {
    const s = JSON.stringify(value);
    return s === undefined ? null : s;
  } catch (_err) {
    return null;
  }
}

/**
 * Does this `itemKind` produce a card the admin can preview?
 *
 * The single source of truth for the question — the dialog gates its whole second column on this,
 * so a kind listed in two places is a kind that eventually disagrees with itself.
 * @param {?string} itemKind
 * @returns {boolean} false for blank/null and for every non-card kind (FIELD_INPUT, CALCULATE, …)
 */
export function hasCardPreview(itemKind) {
  const kind = normKind(itemKind);
  return kind !== '' && PREVIEW_KINDS.includes(kind);
}

/**
 * Build the `POST card/preview` body for the item currently open in the editor.
 *
 * READ-ONLY on `item`: nothing here writes back, normalizes, or defaults anything onto it (the
 * dialog normalizes in `resetForm()` / `onKindChange()` only, and doing it per keystroke has broken
 * this file's caller before).
 *
 * @param {Object} item the dialog's live item — `itemKind`, `fieldKey`, `cardConfigJson`,
 *   `summaryTemplateJson`, `shortFormTemplateJson`, `filingTemplateJson` (strings) and
 *   `shortFormConfig` (object)
 * @param {Object} [opts]
 * @param {?string} [opts.shortFormConfigText] the JSON box's live draft — preferred over
 *   `item.shortFormConfig` whenever it is a non-blank string (rule 2)
 * @param {?string} [opts.lang] render language; 'th' when omitted
 * @returns {?{itemKind:string, cardConfigJson:?string, summaryTemplateJson:?string,
 *   shortFormConfigJson:?string, shortFormTemplateJson:?string, filingTemplateJson:?string,
 *   fieldKey:?string, lang:string}}
 *   `null` for a kind with no card — meaning "there is nothing to preview", not "an error"
 */
export function buildPreviewRequest(item, opts = {}) {
  const src = item && typeof item === 'object' ? item : {};
  const kind = normKind(src.itemKind);
  if (!PREVIEW_KINDS.includes(kind)) return null;

  const options = opts && typeof opts === 'object' ? opts : {};
  const fieldKey = String(src.fieldKey == null ? '' : src.fieldKey).trim();

  const req = {
    itemKind: kind,
    cardConfigJson: null,
    summaryTemplateJson: null,
    shortFormConfigJson: null,
    shortFormTemplateJson: null,
    filingTemplateJson: null,
    simulationTemplateJson: null,
    fieldKey: fieldKey || null,
    lang: options.lang || 'th',
  };

  if (kind === 'SUMMARY') {
    // projector + template engine: the config says which categories to project, the template how to draw them.
    req.cardConfigJson = asJsonText(src.cardConfigJson);
    req.summaryTemplateJson = asJsonText(src.summaryTemplateJson);
  } else if (kind === 'SHORT_FORM') {
    // The FIELD LIST comes from the typed config, always; the template only shapes the frame around it.
    req.shortFormConfigJson = shortFormConfigJson(src, options);
    req.shortFormTemplateJson = asJsonText(src.shortFormTemplateJson);
  } else if (kind === 'FILING_CHOICE') {
    // 🔴 ONE BRANCH PER KIND, never a shared one. Each kind reads exactly one template column, so sending
    //    another kind's would be free bandwidth and an expensive lie: rule 1 — typing in a box this card
    //    never reads would move its signature and re-render a card that did not change. The config is still
    //    COPY (labels + badge) and the template only shapes the frame around what the comparison decided.
    req.cardConfigJson = asJsonText(src.cardConfigJson);
    req.filingTemplateJson = asJsonText(src.filingTemplateJson);
  } else {
    // SIMULATE — the config is MANDATORY (the slider geometry, the compare rows, the savings key) and the
    // template only shapes the envelope around it. It was the last kind to get a box of its own.
    req.cardConfigJson = asJsonText(src.cardConfigJson);
    req.simulationTemplateJson = asJsonText(src.simulationTemplateJson);
  }
  return req;
}

/** The SHORT_FORM config document as it travels — draft first (rule 2), then the item's object. */
function shortFormConfigJson(item, opts) {
  const draft = opts.shortFormConfigText;
  if (typeof draft === 'string' && draft.trim() !== '') return draft;
  const cfg = item.shortFormConfig;
  // A mini-form config is a JSON OBJECT. Anything else (missing, a string, an array) has no config
  // to preview — handing the server a document it cannot read would only spend a round-trip to be
  // told so, and must not throw here either: this runs on every keystroke.
  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return null;
  try {
    const s = JSON.stringify(cfg);
    return s === undefined ? null : s;
  } catch (_err) {
    return null;
  }
}

/**
 * A stable string identity for a request — "has the CARD changed?", asked before spending a render.
 *
 * Built from {@link SIGNATURE_KEYS} in that fixed order and from nothing else, so editing a field
 * the card does not read (`questionText`, prompts, knowledge ids, sort order …) leaves it byte-identical
 * and the panel stays still. Cheap enough to call on every keystroke — that is where it is called.
 * @param {?Object} req from {@link buildPreviewRequest}
 * @returns {string} '' when there is nothing to preview
 */
export function previewSignature(req) {
  if (!req || typeof req !== 'object') return '';
  return JSON.stringify(SIGNATURE_KEYS.map((k) => (req[k] === undefined ? null : req[k])));
}

/**
 * Read the preview response's `raw` — the card's JSON text — into the envelope the card component
 * takes. The counterpart of `utils/taxCardMarker.js` for the one card the editor renders.
 *
 * 🔴 WHY THE SERVER SENDS TEXT AND THIS PARSES IT, RATHER THAN SENDING AN OBJECT.
 * The endpoint used to hand MVC a live Newtonsoft `JObject`, and this app serialises with
 * System.Text.Json: it resolved that object to its `IDictionary<string, JToken>` face (keys intact)
 * and then each VALUE — a `JToken`, which is an `IEnumerable<JToken>` — through the COLLECTION
 * converter, which writes a token's CHILDREN. A scalar has none, so every leaf arrived as `[]`
 * (`{"type":[],"version":[],"refId":[],"data":{…}}`) and the renderer honestly drew
 * "ยังไม่รองรับการ์ดชนิดนี้: []" while the JSON panel, fed by the untouched string, looked perfect.
 * The trap is written up at BAL/BAL/AppBAL/LHB/TaxCardMarkerV2.cs:111-118, and the chat path avoids
 * it by making the token serialise ITSELF before any card meets System.Text.Json. The preview now
 * travels the same way — as text — so there is only ever ONE representation of the card on the wire.
 *
 * 🔴 FAIL-SOFT, NEVER THROWING. The caller's rule 2 ("an error never clears the last good card")
 * depends on this answering rather than raising: a `{ card: null }` leaves the panel's card where it
 * is and only adds a line above it. In practice `raw` is a serialised card and cannot be malformed —
 * the guard is for the day the contract changes again, which is precisely the bug this replaced.
 *
 * Blank input is NOT an error: "the server could not assemble a card" is a thing the server already
 * says in `errors`, and repeating it here in this side's words would double the message.
 *
 * @param {?string} raw the response's `raw`, verbatim
 * @returns {{card: ?Object, error: ?string}} `card` is the `{ type, version, refId, data }` envelope,
 *   or null with `error` set to a Thai line for the panel (null/blank ⇒ both null: nothing to say)
 */
export function parsePreviewCard(raw) {
  if (typeof raw !== 'string' || raw.trim() === '') return { card: null, error: null };

  let doc;
  try {
    doc = JSON.parse(raw);
  } catch (err) {
    return { card: null, error: 'อ่าน JSON ของการ์ดไม่สำเร็จ: ' + ((err && err.message) || 'รูปแบบไม่ถูกต้อง') };
  }

  // A card is an ENVELOPE. An array or a scalar parses fine and is still not one — and handing
  // either to `TaxSummaryCardV2` (whose `card` prop is `type: Object, required: true`) buys a Vue
  // prop warning in the console plus a card drawn from nothing. Say so out loud instead.
  if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
    return { card: null, error: 'ข้อมูลการ์ดที่ได้ไม่ใช่ JSON object — แสดงการ์ดไม่ได้' };
  }
  return { card: doc, error: null };
}

export default {
  PREVIEW_KINDS,
  hasCardPreview,
  buildPreviewRequest,
  previewSignature,
  parsePreviewCard,
};
